import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { Swiper } from "swiper/react";
import { SwiperSlide } from "swiper/react";
import { isDesktopMediaQuery } from '../../constants/constants';
import { fetchInfluencerContent, markExpired, setLoading } from '../../redux-stores/slices/influencerContentSlice';
import { RootState, AppDispatch } from '../../redux-stores/store';
import { MediaType, Post } from '../../types/influencer/influencerTypes';
import { DMAutomation } from '../../types/automations/dmAutomationTypes';
import AddLink from './AddLink';
import { POSTAPIError } from '../../types';
import { ERROR } from '../../constants/errors';
import InsufficientPermissionsCTAModal from '../CTA/InsufficientPermissionsCTAModal';
import DMAutomationsSkeleton from '../../skeletons/DMAutomationsSkeleton';
import { fetchLatestInfluencerMedia, fetchLatestInfluencerSocialPosts } from '../../services/api/influencer';
import { toast } from 'react-toastify';
import { getErrorMessage } from '../../utils/helpers';
import { fetchAvailablePostsThunk, setSelectedSocialAccount } from '../../redux-stores/slices/dmAutomationsListSlice';
import { SocialPost } from '../../types/collaborations/CollaborationTypes';
import IGAccountSelector from '../account/IGAccountSelector';
import RefreshIcon from '../../assets/icons/RefreshIcon';

interface AvailableToLinkProps {

}

const AvailableToLink: React.FC<AvailableToLinkProps> = () => {

    const selectedSocialAccount = useSelector((state: RootState) => state.dmAutomationsList.selectedSocialAccount);
    const connectionRequired = !selectedSocialAccount || selectedSocialAccount.status !== 'connected';


    const igSocialAccounts = useSelector((state: RootState) => state.influencer.social_accounts?.filter(account => account.social_account.platform === 'ig'));

    const [linkingError, setLinkingError] = useState<POSTAPIError | null>(null);

    const isDesktop = useMediaQuery(isDesktopMediaQuery);

    const loading = useSelector((state: RootState) => state.dmAutomationsList.availableToLink.loading);


    const [selectedPost, setSelectedPost] = useState<null | SocialPost>(null);


    const posts = useSelector((state: RootState) => state.dmAutomationsList.availableToLink[selectedSocialAccount?.social_account?.uuid ?? '']?.items ?? []);
    const dispatch = useDispatch<AppDispatch>();


    const fetchAvailablePosts = useCallback(async (force = false) => {
        if (!selectedSocialAccount) return;
        dispatch(fetchAvailablePostsThunk({ socialAccountId: selectedSocialAccount.social_account?.uuid, force }));
    }, [dispatch, selectedSocialAccount])

    useEffect(() => {
        fetchAvailablePosts();
    }, [fetchAvailablePosts, selectedSocialAccount])

    const fetchLatestMedia = async (): Promise<void> => {
        if (!selectedSocialAccount) return;
        try {
            dispatch(setLoading(true));
            await fetchLatestInfluencerSocialPosts(selectedSocialAccount.social_account?.uuid);
        } catch (error: any) {
            console.log(error);
            toast.error(getErrorMessage(error));
        } finally {
            dispatch(markExpired());
            fetchAvailablePosts(true);
        }
    }

    useEffect(() => {
        if (!selectedSocialAccount && igSocialAccounts.length > 0) {
            dispatch(setSelectedSocialAccount(igSocialAccounts[0]));
        }
    }, [selectedSocialAccount, igSocialAccounts])


    if (loading) return <DMAutomationsSkeleton />

    return (

        <div className={`${!connectionRequired ? 'smart-links-section' : ''}`}>
            <div className="d-flex justify-content-between flex-column align-items-center mx-3 mb-3">
                <span className="text-muted fs-14 fw-400">RECENT POSTS</span>
                <span className="text-dark fs-24 fw-600">Available for Linking</span>
            </div>
            <div className="d-flex align-items-center justify-content-between m-3">
                <IGAccountSelector socialAccount={selectedSocialAccount} socialAccounts={igSocialAccounts} onChange={(account) => dispatch(setSelectedSocialAccount(account))} />
                {
                    !connectionRequired &&
                    <button className="refresh-media-btn" onClick={() => fetchLatestMedia()}>
                        <RefreshIcon />
                    </button>
                }
            </div>
            {
                !connectionRequired &&
                <Swiper className="mySwiper px-2"
                    slidesPerView={isDesktop ? 2.2 : 1.2}
                    spaceBetween={10}
                    freeMode={true}
                >
                    {
                        posts.map((post, i) => {
                            return (
                                <SwiperSlide key={i}>
                                    <div className="card card-style m-0 mx-1 shadow-none border p-0" onClick={() => setSelectedPost(post)}>
                                        <div className="p-1">
                                            <img src={post.thumbnail_url} className="rounded-4 post-thumbnail available-to-link-thumbnail" alt="feed" loading='lazy' />
                                        </div>
                                        <div className="swiper-lazy-preloader"></div>
                                        <div className="p-2">
                                            <div className="card-body p-0 text-start">
                                                <span className="d-block text-muted mb-1 font-10">{post.posted_on}</span>
                                                <p style={{ maxHeight: '50px' }}>{post.ig?.caption?.slice(0, 65)}{(post?.ig?.caption?.length ?? 0) > 65 ? '...' : ''}</p>
                                            </div>
                                            <button className="btn btn-outline-primary w-100" onClick={() => setSelectedPost(post)}>
                                                <i className="fa-solid fa-link me-2"></i> Add Link
                                            </button>
                                        </div>

                                    </div>
                                </SwiperSlide>
                            )
                        })
                    }
                    {
                        posts.length > 0 &&
                        <div className='mb-4'></div>
                    }
                    {
                        !loading && posts.length === 0 &&
                        <div className="card h-full card-style rounded-card mx-2">
                            <div className="card-body">
                                <div className="h-200px d-flex justify-content-center align-items-center text-center">
                                    <div className='px-5'>
                                        <h2 className="text-muted fs-18 fw-400">No content available to add a Smart link.</h2>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                </Swiper>
            }
            <AddLink
                isActive={!!selectedPost}
                onClose={() => setSelectedPost(null)}
                post={selectedPost}
                onError={(error) => {
                    setSelectedPost(null)
                    setLinkingError(error)
                }}
            />
            <InsufficientPermissionsCTAModal
                isActive={linkingError?.error_code === ERROR.INSUFFICIENT_PERMISSIONS}
                onClose={() => setLinkingError(null)}
                missing_permissions={linkingError?.missing_permissions!}
                feature={linkingError?.feature!}
            />
        </div>
    )
}

export default AvailableToLink